import React, { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { authEnabled } from '@/config';
import { setAuthToken } from '@/api/client';

export const RETURN_TO_KEY = 'edl-tools.returnTo';

// The pre-login path and query string are stashed in sessionStorage rather
// than OIDC state, so they survive the Hosted UI round trip byte-for-byte.
function AuthGate({ children }: { children: React.ReactNode }) {
  const auth = useAuth();

  // Keep the module-level token fresh DURING render, not in an effect:
  // React runs effects children-first, so an effect here would fire AFTER a
  // child page's first fetch — the initial API call would go out without a
  // bearer and 401 until the next poll. Assigning a module variable in the
  // render body is idempotent and StrictMode-safe.
  setAuthToken(auth.user?.id_token ?? null);

  useEffect(() => {
    if (auth.isLoading || auth.isAuthenticated || auth.activeNavigator || auth.error) {
      return;
    }
    sessionStorage.setItem(
      RETURN_TO_KEY,
      window.location.pathname + window.location.search
    );
    void auth.signinRedirect();
  }, [auth]);

  if (auth.error) {
    return (
      <div className="auth-screen">
        <p>Sign-in failed: {auth.error.message}</p>
        <button onClick={() => void auth.signinRedirect()}>Try again</button>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <div className="auth-screen">Redirecting to sign-in…</div>;
  }

  return <>{children}</>;
}

export default function RequireAuth({ children }: { children: React.ReactNode }) {
  if (!authEnabled) {
    return <>{children}</>;
  }
  return <AuthGate>{children}</AuthGate>;
}
